import { Component } from '@angular/core';
import { ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { AppService } from '@shared/services/app.service';
import { SupportCenterService } from './services/support-center.service';
import {
  SupportCenterContentViewModel,
  SupportCenterPageViewModel,
  SupportCenterCardClick,
  SupportRequestModalData,
} from './model/support-center-model';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';
import { IssueOptions, SousStatus } from '@usf/list-types';
import { SortableSectionHeader } from '@shared/components/sortable-section/models/sortable-section.model';
import {
  ListAnalyticsService,
  SupportCenterConstants,
  SupportCenterSortOptions,
} from '@usf/ngrx-list';
import { FEATURES } from '@shared/constants/splitio-features';
import { UserPhoto } from '@shared/services/photo/model/photo.model';
import { UserFile } from '@shared/services/file/model/file.model';

@Component({
  selector: 'app-support-center',
  templateUrl: './support-center.page.html',
  styleUrls: ['./support-center.page.scss'],
})
export class SupportCenterPage implements ViewWillEnter, ViewWillLeave {
  breadcrumbs: Crumb[];
  viewModel$: Observable<SupportCenterPageViewModel>;
  sousStatus = SousStatus;
  currentFilter = null;
  platformEnum = PlatformEnum;
  supportRequestFlag$: Observable<boolean>;
  supportRequestIssues$: Observable<IssueOptions[]>;
  userEmail$ = this.supportCenterService.email$;
  supportRequestPhotos$: Observable<UserPhoto[]>;
  supportRequestFile: Observable<UserFile>;
  supportRequestModalIsOpen$: BehaviorSubject<boolean>;
  supportCenterModalOptions$: BehaviorSubject<SupportCenterCardClick>;

  constructor(
    readonly platformService: PlatformService,
    private panAppState: PanAppState,
    private translateService: TranslateService,
    private appService: AppService,
    private navigationHelperService: NavigationHelperService,
    private supportCenterService: SupportCenterService,
    private listAnalyticsService: ListAnalyticsService,
  ) {
    this.supportRequestIssues$ =
      this.supportCenterService.getSupportRequestIssues$();
    this.supportRequestPhotos$ =
      this.supportCenterService.supportRequestPhotos$;
    this.supportRequestModalIsOpen$ =
      this.supportCenterService.supportRequestModalIsOpen$;
    this.supportCenterModalOptions$ =
      this.supportCenterService.supportCenterModalOptions$;
  }

  ionViewWillLeave(): void {
    this.viewModel$ = undefined;
  }

  ionViewWillEnter() {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.supportCenter),
    );
    const homeCrumbUrl = this.platformService.isTouch.value
      ? '/touch/home'
      : '/desktop/home';

    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: homeCrumbUrl,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.supportCenter',
        url: '/support-center',
        active: true,
      },
    ];
    this.getViewModel();

    // start with default sort
    this.updateSupportCenterSort({
      headerName: SupportCenterSortOptions.status,
      sortDirection: SupportCenterConstants.asc,
    });
    this.supportRequestFlag$ = this.panAppState.feature$([
      FEATURES.split_global_support_request.name,
    ]);
  }

  ionViewDidEnter() {
    this.viewModel$
      .pipe(
        filter(vm => vm.isLoaded),
        take(1),
      )
      .subscribe(vm => {
        this.listAnalyticsService.trackSupportCenterPageLoaded(
          vm.supportItems.length,
          vm.supportItems.filter(item => item.status === SousStatus.FAILED)
            .length,
        );
      });
  }

  getViewModel = () => {
    this.viewModel$ = this.supportCenterService.serviceViewModel$(
      this.platformService.platformType,
    );
  };

  goToCreditRequest() {
    this.navigationHelperService.routeTo(PATHS.REQUEST_CREDIT);
  }

  goToHelpCenter() {
    this.appService.openUrlInBrowser(
      'https://www.usfoods.com/our-services/moxe-help-center.html',
    );
  }

  trackItem = (index: number, item: SupportCenterContentViewModel) => {
    return item ? item?.caseId : index;
  };

  selectFilter = (status?: SousStatus, index?: number) => {
    this.scrollToSingleGroup(index);
    this.currentFilter = status;
    this.supportCenterService.updateFilter(status);
  };

  updateSupportCenterSort = (newSort: SortableSectionHeader) => {
    this.supportCenterService.updateSort(newSort);
  };

  handleResubmitRequest = (card: SupportCenterContentViewModel) => {
    this.supportCenterService.requestResubmit(card.mongoRecordId);
    this.listAnalyticsService.trackSupportCenterResubmitRequest(
      card.serviceRequestType,
    );
  };

  showSupportCenterModal = (supportItem: SupportCenterContentViewModel) => {
    const cardModalInfo: SupportCenterCardClick = {
      isOpen: true,
      supportItem,
    };
    this.supportCenterService.showSupportCenterModal(cardModalInfo);
  };

  closeSupportCenterModal = () => {
    this.supportCenterService.closeSupportCenterModal();
  };

  scrollToSingleGroup(index: number): void {
    if (this.platformService.isTouch.value) {
      const tabContainer = document.querySelector(
        '.tab-container',
      ) as HTMLElement;
      const tabItems = document.querySelectorAll(
        '.filter-tab',
      ) as NodeListOf<HTMLElement>;

      if (tabContainer && tabItems) {
        tabContainer.classList.toggle('add-bounce');
        tabContainer.scrollTo({
          left: tabItems[index].offsetLeft - 50,
          behavior: 'smooth',
        });
      }
      setTimeout(() => {
        tabContainer.classList.remove('add-bounce');
      }, 500);
    }
  }

  showSupportRequestModal = (showModal: boolean) => {
    this.supportCenterService.showSupportRequestModal(showModal);
  };

  onAddPhoto = () => {
    this.supportCenterService.onAddPhoto();
  };

  onDeletePhoto = (photo: UserPhoto) => {
    this.supportCenterService.onDeletePhoto(photo);
  };

  submitSupportRequest = (modalData: SupportRequestModalData) => {
    this.supportCenterService.submitSupportRequest(modalData);
    this.listAnalyticsService.trackSubmitSupportRequest(
      modalData?.requestReason?.displayText ?? '',
    );
  };
}
